/** Modal dialog, confirmation and toast. */
import { useEffect } from "react";
import type { ReactNode } from "react";

import { Button, Icon, IconButton } from "./primitives";

export function Modal({
  title,
  description,
  onClose,
  children,
}: {
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="scrim" onClick={onClose} role="presentation">
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal__head">
          <div className="u-row">
            <div className="modal__title" style={{ flex: 1 }}>
              {title}
            </div>
            <IconButton icon="close" label="Close" size="sm" onClick={onClose} />
          </div>
          {description && <div className="modal__desc">{description}</div>}
        </div>
        <div className="modal__body">{children}</div>
      </div>
    </div>
  );
}

/**
 * Ask before doing something that cannot be undone.
 *
 * Deliberately not `window.confirm`: this app's one destructive action deletes a
 * whole selection, and the user is owed the count and the names in front of them
 * while they decide, not a browser string.
 */
export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  confirmIcon,
  busy,
  onConfirm,
  onClose,
  children,
}: {
  title: string;
  description?: string;
  confirmLabel: string;
  confirmIcon?: string;
  /** True while the action runs, so it cannot be started twice. */
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  /** Optional detail - what exactly is about to go. */
  children?: ReactNode;
}) {
  return (
    <Modal title={title} description={description} onClose={onClose}>
      {children}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="danger" icon={confirmIcon} onClick={onConfirm} disabled={busy}>
          {busy ? "Working..." : confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}

export interface ToastState {
  title: string;
  caption?: string;
  tone?: "success" | "danger";
}

export function Toast({ toast, onDismiss }: { toast: ToastState; onDismiss: () => void }) {
  useEffect(() => {
    const timer = window.setTimeout(onDismiss, 8000);
    return () => window.clearTimeout(timer);
  }, [toast, onDismiss]);

  const danger = toast.tone === "danger";
  return (
    <div className="toast" role="status">
      <Icon
        name={danger ? "error" : "check_circle"}
        size={20}
        className={danger ? "" : ""}
      />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="toast__title">{toast.title}</div>
        {toast.caption && <div className="toast__caption">{toast.caption}</div>}
      </div>
      <IconButton icon="close" label="Dismiss" size="sm" onClick={onDismiss} />
    </div>
  );
}
